import { RecommendationContext } from '@theme/recently-viewed-products';

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!id) {
      throw new Error('An ID attribute is required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    const productIds = this.#resolveProductIds(productId);

    if (productIds.length === 0) {
      if (!this.#renderManualFallback()) {
        this.#handleError(new Error('Product ID and an ID attribute are required'));
      }
      return;
    }

    this.#tryLoadRecommendations(productIds, sectionId, intent, id)
      .then((didRenderRecommendations) => {
        if (!didRenderRecommendations && !this.#renderManualFallback()) {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (!this.#renderManualFallback()) {
          this.#handleError(e);
        }
      });
  }

  /**
   * Attempts to fetch recommendations from a chain of product ids.
   * @param {string[]} productIds
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @param {string} id
   * @returns {Promise<boolean>}
   */
  async #tryLoadRecommendations(productIds, sectionId, intent, id) {
    for (const candidateProductId of productIds) {
      const result = await this.#fetchCachedRecommendations(candidateProductId, sectionId, intent);

      if (!result.success) {
        continue;
      }

      const html = document.createElement('div');
      html.innerHTML = result.data || '';
      const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

      if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
        this.dataset.recommendationsPerformed = 'true';
        this.dataset.productId = candidateProductId;
        this.classList.remove('hidden');
        this.innerHTML = recommendations.innerHTML;
        return true;
      }
    }

    return false;
  }

  /**
   * Resolves product ids for recommendations, including empty-cart fallbacks.
   * @param {string | undefined} productId
   * @returns {string[]}
   */
  #resolveProductIds(productId) {
    const productIds = [];
    const contextSources = (this.dataset.contextSources || '')
      .split(',')
      .map((source) => source.trim())
      .filter(Boolean);

    if (this.#isValidProductId(productId)) {
      productIds.push(productId);
    }

    for (const source of contextSources) {
      if (source === 'last-added') {
        productIds.push(RecommendationContext.getLastAddedProduct());
      } else if (source === 'last-viewed') {
        productIds.push(RecommendationContext.getLastViewedProduct());
      }
    }

    return [...new Set(productIds.filter((candidate) => this.#isValidProductId(candidate)))];
  }

  /**
   * Renders the preloaded manual fallback if available.
   * @returns {boolean}
   */
  #renderManualFallback() {
    const template = this.querySelector('template[data-manual-fallback]');
    if (!(template instanceof HTMLTemplateElement)) return false;

    this.dataset.recommendationsPerformed = 'true';
    this.classList.remove('hidden');
    this.innerHTML = template.innerHTML;
    return true;
  }

  /**
   * @param {string | undefined | null} productId
   * @returns {productId is string}
   */
  #isValidProductId(productId) {
    return Boolean(productId && productId !== '0');
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
